/**
 * Has this player actually PLAYED yet?
 *
 * **THE PROBLEM THIS EXISTS TO FIX.** Every optional nudge in the app fired the moment an
 * acting identity appeared — which, for a new player, is the instant their adoption
 * completes. So the first thing a brand-new trainer saw, before they had touched their own
 * Suikamon once, was their pet asking them to go join a Telegram group. Inviting somebody to
 * the clubhouse before they have played a single round is a request they have no reason to
 * say yes to, and it is a modal in front of the thing they actually came for.
 *
 * So a nudge waits for evidence of play. Not a timer — a timer measures how long a modal has
 * been postponed, not whether the player has done anything — but a COUNT OF REAL ACTIONS:
 * care actions and battle turns, recorded at the two choke points every one of them passes
 * through (`usePetActions.executeAction` and `pvpApi.executeMove`).
 *
 * Deliberately local-only. This gates cosmetic popups, so a browser that blocks storage
 * should fail toward SILENCE rather than toward nagging — every failure path below returns
 * "not yet".
 */

/** Where the count lives. Per browser, like every other one-time-popup marker in the app. */
const STORAGE_KEY = 'suikamon_player_actions';

/**
 * How many real actions buy the right to interrupt.
 *
 * Three, because that is the length of the first thing a player does with a new Suikamon —
 * feed it, clean up after it, play with it — and a pet that speaks up after you have looked
 * after it three times is a pet you have a reason to listen to.
 */
export const NUDGE_MIN_PLAYER_ACTIONS = 3;

/**
 * Fired on every recorded action so a listener can re-check without polling.
 *
 * The alternative was for `App` to read localStorage on an interval, or to only re-check on
 * an identity change — and an identity change is precisely the moment this module exists to
 * stop reacting to, so the count would never be re-read within a session and a player would
 * have to reload before a nudge could ever appear.
 */
export const PLAYER_ACTION_EVENT = 'suikamon:player-action';

/**
 * The calendar day of the player's FIRST recorded action.
 *
 * Three actions turned out to be three battle turns — so a brand-new guest met the "follow
 * us on X" modal a few minutes in, straight after their first fight, as the fourth popup of
 * the session. An invitation lands on a player who came BACK, not one still finding the
 * buttons: optional nudges now also wait for a later day than the first one played.
 */
const FIRST_PLAY_DAY_KEY = 'suikamon_first_play_day';

function localDayStamp(date: Date = new Date()): string {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}

/**
 * True unless the stored first-play day is TODAY. A missing stamp means a player who
 * predates it (their count saturated before the stamp existed) — they are returning by
 * definition. Unreadable storage fails toward silence, like every gate in this module.
 */
function isReturningVisit(): boolean {
  try {
    const firstDay = localStorage.getItem(FIRST_PLAY_DAY_KEY);
    return firstDay === null || firstDay !== localDayStamp();
  } catch {
    return false;
  }
}

export function getPlayerActionCount(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const count = raw === null ? 0 : Number.parseInt(raw, 10);
    return Number.isFinite(count) && count > 0 ? count : 0;
  } catch {
    return 0;
  }
}

/**
 * One real thing the player did. Safe to call from anywhere and safe to call often — it
 * saturates at the threshold rather than counting forever, so the stored value stays a small
 * number and a long-running player is not writing to storage on every single turn.
 */
export function recordPlayerAction(): void {
  const count = getPlayerActionCount();
  if (count >= NUDGE_MIN_PLAYER_ACTIONS) return;

  try {
    if (count === 0) localStorage.setItem(FIRST_PLAY_DAY_KEY, localDayStamp());
    localStorage.setItem(STORAGE_KEY, String(count + 1));
  } catch {
    // Storage blocked (private mode / quota). Nothing to do: the nudge simply never fires,
    // which is the right way for a cosmetic popup to fail.
    return;
  }

  try {
    window.dispatchEvent(new CustomEvent(PLAYER_ACTION_EVENT));
  } catch {
    // No window (SSR / prerender) — the count is stored, the listener will read it on mount.
  }
}

// ============================================================================
// The first-battle invitation
// ============================================================================

/**
 * Battles are the best part of the game, and a new player who only ever feeds and cleans can
 * leave before finding them: the Battle button is one of eight on the pet screen. So after a
 * couple of care actions the pet screen invites them — inline, not a modal — to a first fight.
 */
const CARE_ACTIONS_KEY = 'suikamon_care_actions';
const HAS_BATTLED_KEY = 'suikamon_has_battled';

/** Care actions before the invitation shows: feed it, then one more thing — then a fight. */
export const FIRST_BATTLE_INVITE_AFTER_CARE = 2;

function readCount(key: string): number {
  try {
    const raw = localStorage.getItem(key);
    const count = raw === null ? 0 : Number.parseInt(raw, 10);
    return Number.isFinite(count) && count > 0 ? count : 0;
  } catch {
    return 0;
  }
}

function announce(): void {
  try {
    window.dispatchEvent(new CustomEvent(PLAYER_ACTION_EVENT));
  } catch {
    // No window (SSR / prerender).
  }
}

/** One successful care action. Saturates at the invitation threshold. */
export function recordCareAction(): void {
  const count = readCount(CARE_ACTIONS_KEY);
  if (count >= FIRST_BATTLE_INVITE_AFTER_CARE) return;
  try {
    localStorage.setItem(CARE_ACTIONS_KEY, String(count + 1));
  } catch {
    return;
  }
  announce();
}

/** The player has fought (a move was committed) or waved the invitation away. Final. */
export function markFirstBattleDone(): void {
  try {
    if (localStorage.getItem(HAS_BATTLED_KEY) === '1') return;
    localStorage.setItem(HAS_BATTLED_KEY, '1');
  } catch {
    return;
  }
  announce();
}

/** True when the pet screen should invite this player to their first battle. Fails silent. */
export function shouldInviteFirstBattle(): boolean {
  try {
    if (localStorage.getItem(HAS_BATTLED_KEY) === '1') return false;
  } catch {
    return false;
  }
  return readCount(CARE_ACTIONS_KEY) >= FIRST_BATTLE_INVITE_AFTER_CARE;
}

/**
 * True once the player has done a few real things in this browser — care or battle turns.
 * The daily reward asks this (not the stricter nudge gate below): a reward belongs to the
 * first session too, just not as the first thing a new trainer sees before meeting their
 * Suikamon. A returning player's count is already saturated, so it opens for them at once.
 */
export function hasTakenFirstActions(): boolean {
  return getPlayerActionCount() >= NUDGE_MIN_PLAYER_ACTIONS;
}

/**
 * **THE GATE EVERY OPTIONAL POPUP ASKS.** True once the player has done enough to have an
 * opinion about the game. Anything that is not essential to the action in front of them —
 * a social invite, a season announcement — must wait for this: real play, AND a visit on a
 * later day than the first one (see FIRST_PLAY_DAY_KEY).
 */
export function hasPlayedEnoughForNudges(): boolean {
  return hasTakenFirstActions() && isReturningVisit();
}
